import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import { Play, Pause, RotateCcw, Volume2, VolumeX, ChevronLeft, Clock, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';
import { toast } from 'sonner';

type BreathPattern = 'coherence' | 'box' | 'calm' | 'energise' | 'sigh' | 'sleep';
type ExerciseStep = 'symptoms-before' | 'breathing' | 'symptoms-after' | 'complete';

interface BreathworkExperienceProps {
  onClose?: () => void;
  defaultPattern?: BreathPattern;
  skipTracking?: boolean;
}

const PATTERNS = {
  coherence: {
    name: 'Coherence',
    subtitle: 'Balance & Flow',
    description: 'Equal breathing for heart-brain harmony',
    instructions: 'Breathe in and out through your nose. Nasal breathing filters air, activates the diaphragm, and helps maintain calm by stimulating the vagus nerve.',
    inhale: 5,
    hold1: 0,
    exhale: 5,
    hold2: 0,
    color: 'from-secondary to-secondary/80',
    bgGradient: 'from-secondary/40 via-secondary/30 to-background',
    particleColor: 'hsl(182, 37%, 46%)',
  },
  box: {
    name: 'Box Breathing',
    subtitle: 'Focus & Control',
    description: 'Navy SEAL technique for mental clarity',
    instructions: 'Breathe in and out through your nose. The equal phases create a rhythmic pattern that steadies the mind and regulates the autonomic nervous system.',
    inhale: 4,
    hold1: 4,
    exhale: 4,
    hold2: 4,
    color: 'from-primary to-primary/80',
    bgGradient: 'from-primary/40 via-primary/30 to-background',
    particleColor: 'hsl(42, 34%, 73%)',
  },
  calm: {
    name: 'Deep Calm',
    subtitle: 'Relaxation',
    description: 'Extended exhale for deep relaxation',
    instructions: 'Breathe in through your nose, out through your mouth with a soft "haaa" sound. The extended exhale activates the parasympathetic nervous system, slowing heart rate.',
    inhale: 4,
    hold1: 2,
    exhale: 8,
    hold2: 0,
    color: 'from-muted to-muted/80',
    bgGradient: 'from-muted/40 via-muted/30 to-background',
    particleColor: 'hsl(177, 41%, 76%)',
  },
  energise: {
    name: 'Energise',
    subtitle: 'Awakening',
    description: 'Quick breaths to boost energy',
    instructions: 'Breathe in and out through your nose with quick, rhythmic breaths. This activates the sympathetic nervous system and increases alertness and oxygen flow.',
    inhale: 3,
    hold1: 0,
    exhale: 3,
    hold2: 0,
    color: 'from-accent to-accent/80',
    bgGradient: 'from-accent/40 via-accent/30 to-background',
    particleColor: 'hsl(183, 92%, 51%)',
  },
  sigh: {
    name: 'Physiological Sigh',
    subtitle: 'Instant Calm',
    description: 'Double inhale + long exhale for rapid stress relief',
    instructions: 'Inhale deeply through your nose, then take a second shorter inhale to fully expand your lungs. Exhale slowly through your mouth. This reinflates collapsed air sacs and triggers rapid calm.',
    inhale: 3,
    hold1: 1,
    exhale: 6,
    hold2: 1,
    color: 'from-secondary to-accent',
    bgGradient: 'from-secondary/40 via-accent/30 to-background',
    particleColor: 'hsl(42, 44%, 66%)',
  },
  sleep: {
    name: '4-7-8 Breathing',
    subtitle: 'Sleep & Deep Relaxation',
    description: 'Dr. Weil\'s technique for deep relaxation & sleep',
    instructions: 'Breathe in quietly through your nose. Hold your breath gently. Exhale completely through your mouth, making a soft "whoosh" sound. The long hold and exhale slow your heart and prepare the body for rest.',
    inhale: 4,
    hold1: 7,
    exhale: 8,
    hold2: 0,
    color: 'from-primary to-muted',
    bgGradient: 'from-primary/40 via-muted/30 to-background',
    particleColor: 'hsl(182, 29%, 26%)',
  },
};

const DURATIONS = [
  { label: '2 min', value: 120 },
  { label: '5 min', value: 300 },
  { label: '10 min', value: 600 },
  { label: '15 min', value: 900 },
];

export default function BreathworkExperience({ onClose, defaultPattern = 'coherence', skipTracking = false }: BreathworkExperienceProps) {
  const { logExercise, isLogging } = useUniversalTracking();
  
  const [exerciseStep, setExerciseStep] = useState<ExerciseStep>(skipTracking ? 'breathing' : 'symptoms-before');
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const [sessionStartTime, setSessionStartTime] = useState<number>(Date.now());
  
  const [selectedPattern, setSelectedPattern] = useState<BreathPattern>(defaultPattern);
  const [selectedDuration, setSelectedDuration] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [phase, setPhase] = useState<'inhale' | 'hold1' | 'exhale' | 'hold2'>('inhale');
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(selectedDuration);
  const [breathCount, setBreathCount] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [showCompletion, setShowCompletion] = useState(false);
  
  const circleControls = useAnimationControls();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const phaseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const pattern = PATTERNS[selectedPattern];
  const cycleDuration = pattern.inhale + pattern.hold1 + pattern.exhale + pattern.hold2;

  const handleFinalComplete = async () => {
    const duration = Math.round((Date.now() - sessionStartTime) / 1000 / 60);
    
    try {
      await logExercise({
        exercise_type: 'breathwork',
        exercise_name: `Breathwork - ${pattern.name}`,
        category: 'breathwork',
        duration_minutes: duration,
        symptoms_before: symptomsBefore,
        symptoms_after: symptomsAfter,
        completion_data: { 
          pattern: selectedPattern,
          breathCount,
          targetDuration: selectedDuration
        }
      });
      setExerciseStep('complete');
    } catch (error) {
      console.error('Error logging exercise:', error);
      toast.error('Failed to save exercise');
    }
  };

  // Audio functions
  const playTone = useCallback((frequency: number, duration: number, type: 'soft' | 'chime' = 'soft') => {
    if (!audioEnabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      oscillator.type = type === 'chime' ? 'triangle' : 'sine';
      
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(type === 'chime' ? 0.15 : 0.08, ctx.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      // Audio not supported
    }
  }, [audioEnabled]);

  // Run breathing cycle
  const runPhase = useCallback(() => {
    if (!isPlaying) return;

    const phases: Array<'inhale' | 'hold1' | 'exhale' | 'hold2'> = ['inhale', 'hold1', 'exhale', 'hold2'];
    const durations = [pattern.inhale, pattern.hold1, pattern.exhale, pattern.hold2];
    
    let currentPhaseIndex = 0;
    
    const executePhase = () => {
      if (!isPlaying) return;
      
      const currentPhase = phases[currentPhaseIndex];
      const duration = durations[currentPhaseIndex];
      
      if (duration === 0) {
        currentPhaseIndex = (currentPhaseIndex + 1) % 4;
        if (currentPhaseIndex === 0) {
          setBreathCount(prev => prev + 1);
        }
        executePhase();
        return;
      }
      
      setPhase(currentPhase);
      setPhaseProgress(0);
      
      // Play transition tone
      if (currentPhase === 'inhale') {
        playTone(440, 0.5, 'chime');
      } else if (currentPhase === 'exhale') {
        playTone(330, 0.5, 'chime');
      }
      
      // Animate circle
      if (currentPhase === 'inhale') {
        circleControls.start({
          scale: 1.5,
          transition: { duration, ease: 'easeInOut' }
        });
      } else if (currentPhase === 'exhale') {
        circleControls.start({
          scale: 1,
          transition: { duration, ease: 'easeInOut' }
        });
      }
      
      // Progress animation
      let elapsed = 0;
      const progressInterval = setInterval(() => {
        elapsed += 100;
        setPhaseProgress((elapsed / (duration * 1000)) * 100);
        
        if (elapsed >= duration * 1000) {
          clearInterval(progressInterval);
          currentPhaseIndex = (currentPhaseIndex + 1) % 4;
          if (currentPhaseIndex === 0) {
            setBreathCount(prev => prev + 1);
          }
          executePhase();
        }
      }, 100);
      
      phaseTimerRef.current = progressInterval as unknown as NodeJS.Timeout;
    };
    
    executePhase();
  }, [isPlaying, pattern, circleControls, playTone]);

  // Start/stop session
  useEffect(() => {
    if (isPlaying) {
      runPhase();
      
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsPlaying(false);
            setShowCompletion(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (phaseTimerRef.current) clearInterval(phaseTimerRef.current);
    };
  }, [isPlaying, runPhase]);

  const startSession = () => {
    setIsSessionActive(true);
    setTimeRemaining(selectedDuration);
    setBreathCount(0);
    setPhase('inhale');
    setPhaseProgress(0);
    setTimeout(() => setIsPlaying(true), 500);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const resetSession = () => {
    setIsPlaying(false);
    setIsSessionActive(false);
    setShowCompletion(false);
    setTimeRemaining(selectedDuration);
    setBreathCount(0);
    setPhase('inhale');
    setPhaseProgress(0);
    circleControls.start({ scale: 1 });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseText = () => {
    switch (phase) {
      case 'inhale': return 'Breathe In';
      case 'hold1': return selectedPattern === 'sigh' ? 'Inhale Again' : 'Hold';
      case 'exhale': return 'Breathe Out';
      case 'hold2': return 'Hold';
    }
  };

  const getPhaseDuration = () => {
    switch (phase) {
      case 'inhale': return pattern.inhale;
      case 'hold1': return pattern.hold1;
      case 'exhale': return pattern.exhale;
      case 'hold2': return pattern.hold2;
    }
  };

  // Symptoms Before Step
  if (exerciseStep === 'symptoms-before') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-background p-6 pb-safe-area-inset-bottom"
      >
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="mb-4">
            <ChevronLeft className="w-6 h-6" />
          </Button>
        )}

        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <CardTitle className="text-2xl font-light">Breathwork</CardTitle>
            <CardDescription>
              Before we begin, let's check in with how you're feeling.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UniversalSymptomTracker
              mode="before"
              onRatingsChange={setSymptomsBefore}
            />
            
            <Button 
              onClick={() => {
                setSessionStartTime(Date.now());
                setExerciseStep('breathing');
              }} 
              className="w-full"
              disabled={Object.keys(symptomsBefore).length === 0}
            >
              Continue to Breathwork
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Symptoms After Step
  if (exerciseStep === 'symptoms-after') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-background p-6 pb-safe-area-inset-bottom flex items-center justify-center"
      >
        <Card className="bg-gradient-therapeutic border-primary/20 max-w-lg w-full">
          <CardHeader>
            <CardTitle className="text-xl font-light">How Are You Feeling Now?</CardTitle>
            <CardDescription>
              After completing {breathCount} breath cycles, take a moment to check in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UniversalSymptomTracker
              mode="after"
              previousRatings={symptomsBefore}
              onRatingsChange={setSymptomsAfter}
            />
            
            <Button 
              onClick={handleFinalComplete} 
              className="w-full"
              disabled={isLogging}
            >
              {isLogging ? 'Saving...' : 'Complete Exercise'}
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Final Complete Step
  if (exerciseStep === 'complete') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-background"
      >
        <div className="text-center px-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="mb-8"
          >
            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-background" />
            </div>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-3xl font-bold mb-2"
          >
            Session Complete
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-muted-foreground mb-8"
          >
            Your progress has been tracked and saved.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-2 gap-4 max-w-xs mx-auto mb-8"
          >
            <div className="p-4 rounded-xl bg-muted/30">
              <p className="text-2xl font-bold text-primary">{Math.floor(selectedDuration / 60)}</p>
              <p className="text-xs text-muted-foreground">minutes</p>
            </div>
            <div className="p-4 rounded-xl bg-muted/30">
              <p className="text-2xl font-bold text-primary">{breathCount}</p>
              <p className="text-xs text-muted-foreground">breaths</p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Button onClick={onClose} size="lg">
              Done
            </Button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Breathing completion - transition to symptoms-after
  if (showCompletion) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-background"
      >
        <div className="text-center px-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="mb-8"
          >
            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-background" />
            </div>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-3xl font-bold mb-2"
          >
            Breathing Complete
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-muted-foreground mb-8"
          >
            You completed {breathCount} breath cycles
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {skipTracking ? (
              <Button onClick={onClose} size="lg">
                Done
              </Button>
            ) : (
              <Button onClick={() => setExerciseStep('symptoms-after')} size="lg" className="gap-2">
                Rate How You Feel Now
              </Button>
            )}
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Active breathing session
  if (isSessionActive) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b ${pattern.bgGradient}`}
      >
        {/* Ambient particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{ 
                backgroundColor: pattern.particleColor,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.5, 0.2],
                scale: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-safe-area-inset-top pt-4 left-0 right-0 flex items-center justify-between px-6">
          <Button variant="ghost" size="icon" onClick={resetSession}>
            <ChevronLeft className="w-6 h-6" />
          </Button>
          
          <div className="text-center">
            <p className="text-sm text-muted-foreground">{pattern.name}</p>
            <p className="text-lg font-semibold">{formatTime(timeRemaining)}</p>
          </div>
          
          <Button variant="ghost" size="icon" onClick={() => setAudioEnabled(!audioEnabled)}>
            {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </Button>
        </div>

        {/* Main breathing circle */}
        <div className="relative flex items-center justify-center">
          {/* Outer pulse rings */}
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: cycleDuration, repeat: Infinity }}
            className={`absolute w-80 h-80 rounded-full bg-gradient-to-r ${pattern.color} blur-3xl`}
          />
          
          {/* Main breathing circle */}
          <motion.div
            animate={circleControls}
            initial={{ scale: 1 }}
            className={`relative w-64 h-64 rounded-full bg-gradient-to-br ${pattern.color} shadow-2xl flex items-center justify-center`}
            style={{
              boxShadow: `0 0 60px ${pattern.particleColor}40`,
            }}
          >
            {/* Inner glow */}
            <div className="absolute inset-4 rounded-full bg-background/10 backdrop-blur-sm" />
            
            {/* Phase text */}
            <div className="relative z-10 text-center">
              <motion.p
                key={phase}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl font-light text-white tracking-wide"
              >
                {getPhaseText()}
              </motion.p>
              <p className="text-5xl font-bold text-white/90 mt-2">
                {(() => {
                  const duration = getPhaseDuration();
                  const elapsed = Math.floor((phaseProgress / 100) * duration);
                  // Inhale: count up (1, 2, 3...) - building breath
                  // Exhale: count down (5, 4, 3...) - releasing breath
                  // Hold: count up (1, 2, 3...) - counting through
                  if (phase === 'exhale') {
                    return Math.max(1, duration - elapsed);
                  } else {
                    return Math.min(duration, elapsed + 1);
                  }
                })()}
              </p>
            </div>
          </motion.div>
          
          {/* Progress ring */}
          <svg className="absolute w-72 h-72" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="48"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.5"
              className="text-white/20"
            />
            <motion.circle
              cx="50"
              cy="50"
              r="48"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="text-white/60"
              strokeDasharray={301.59}
              strokeDashoffset={301.59 - (phaseProgress / 100) * 301.59}
              transform="rotate(-90 50 50)"
            />
          </svg>
        </div>

        {/* Controls */}
        <div className="absolute bottom-safe-area-inset-bottom pb-8 flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground">
            Breath {breathCount + 1}
          </p>
          
          <Button
            size="lg"
            variant="outline"
            className="w-16 h-16 rounded-full"
            onClick={togglePlay}
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-1" />
            )}
          </Button>
        </div>
      </motion.div>
    );
  }

  // Pattern selection screen
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-background p-6 pb-safe-area-inset-bottom"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ChevronLeft className="w-6 h-6" />
          </Button>
        )}
        <h1 className="text-2xl font-bold flex-1 text-center">Breathwork</h1>
        <div className="w-10" />
      </div>

      {/* Pattern Cards */}
      <div className="space-y-3 mb-8">
        <p className="text-sm text-muted-foreground mb-4">Choose your practice</p>
        
        {(Object.entries(PATTERNS) as [BreathPattern, typeof PATTERNS.coherence][]).map(([key, pat]) => (
          <motion.button
            key={key}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedPattern(key)}
            className={`w-full p-4 rounded-2xl text-left transition-all ${
              selectedPattern === key 
                ? `bg-gradient-to-r ${pat.color} text-white shadow-lg` 
                : 'bg-card/50 border border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{pat.name}</p>
                <p className={`text-sm ${selectedPattern === key ? 'text-white/80' : 'text-muted-foreground'}`}>
                  {pat.subtitle}
                </p>
              </div>
              <div className={`text-right text-sm ${selectedPattern === key ? 'text-white/80' : 'text-muted-foreground'}`}>
                <p>{pat.inhale}-{pat.hold1}-{pat.exhale}-{pat.hold2}</p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Duration Selection */}
      <div className="mb-8">
        <p className="text-sm text-muted-foreground mb-4">Duration</p>
        <div className="flex gap-2">
          {DURATIONS.map((dur) => (
            <button
              key={dur.value}
              onClick={() => setSelectedDuration(dur.value)}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                selectedDuration === dur.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              {dur.label}
            </button>
          ))}
        </div>
      </div>

      {/* Pattern Description & Instructions - shown before button */}
      <div className="text-center mb-8 space-y-3">
        <p className="text-sm text-muted-foreground">
          {pattern.description}
        </p>
        <p className="text-sm text-foreground/80 bg-muted/50 rounded-lg p-3 max-w-md mx-auto">
          {pattern.instructions}
        </p>
      </div>

      {/* Start Button */}
      <Button
        size="lg"
        className="w-full h-14 text-lg gap-2"
        onClick={startSession}
      >
        <Play className="w-5 h-5" />
        Start Session
      </Button>
    </motion.div>
  );
}
